import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// API calls ALWAYS go to the main app domain, even when loaded from a custom domain
const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://app.salesqualifyd.com';

// ─── Component Renderer ───────────────────────────────────────────────────────

function FunnelHeadline({ content, styles }) {
  const sizeMap = { lg: '2rem', xl: '2.5rem', '2xl': '3rem', '3xl': '3.5rem' };
  return (
    <div style={{ textAlign: content?.align || 'center', padding: `${styles?.padding || 16}px`, margin: `${styles?.margin || 0}px`, backgroundColor: styles?.backgroundColor || 'transparent' }}>
      <h1 style={{ fontSize: sizeMap[content?.size] || '2.5rem', fontWeight: 700, color: styles?.textColor || '#111827', lineHeight: 1.2 }}>
        {content?.text || 'Your Headline Here'}
      </h1>
    </div>
  );
}

function FunnelText({ content, styles }) {
  return (
    <div style={{ textAlign: content?.align || 'left', padding: `${styles?.padding || 16}px`, margin: `${styles?.margin || 0}px`, backgroundColor: styles?.backgroundColor || 'transparent' }}>
      <p style={{ fontSize: '1.1rem', lineHeight: 1.7, color: styles?.textColor || '#374151', whiteSpace: 'pre-wrap' }}>
        {content?.text || ''}
      </p>
    </div>
  );
}

function FunnelImage({ content, styles }) {
  if (!content?.url) return null;
  return (
    <div style={{ textAlign: 'center', padding: `${styles?.padding || 8}px`, margin: `${styles?.margin || 0}px` }}>
      <img src={content.url} alt={content.alt || ''} style={{ maxWidth: content.width || '100%', height: 'auto', borderRadius: 8 }} />
    </div>
  );
}

function FunnelVideo({ content, styles }) {
  if (!content?.url) return null;
  let embedUrl = content.url;
  if (content.url.includes('youtube.com/watch')) {
    const vid = new URL(content.url).searchParams.get('v');
    embedUrl = `https://www.youtube.com/embed/${vid}${content.autoplay ? '?autoplay=1' : ''}`;
  } else if (content.url.includes('youtu.be/')) {
    const vid = content.url.split('youtu.be/')[1];
    embedUrl = `https://www.youtube.com/embed/${vid}`;
  } else if (content.url.includes('vimeo.com/')) {
    const vid = content.url.split('vimeo.com/')[1];
    embedUrl = `https://player.vimeo.com/video/${vid}`;
  }
  return (
    <div style={{ padding: `${styles?.padding || 8}px`, margin: `${styles?.margin || 0}px` }}>
      <div style={{ position: 'relative', paddingTop: '56.25%' }}>
        <iframe src={embedUrl} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0, borderRadius: 8 }} allowFullScreen title="Funnel Video" />
      </div>
    </div>
  );
}

function FunnelButton({ content, styles, funnelId, pageSlug, funnelSlug, workspaceId, nextPageSlug }) {
  const navigate = useNavigate();
  const btnStyle = {
    background: styles?.backgroundColor || content?.backgroundColor || '#FF6600',
    color: styles?.textColor || content?.textColor || '#fff',
  };
  
  const handleClick = async () => {
    if (funnelId) {
      fetch(`${API_URL}/api/funnels/${funnelId}/analytics/track`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_type: 'conversion', page_slug: pageSlug })
      }).catch(() => {});
    }
    const href = content?.url || '';
    if (!href || href === '#' || href === '#next-step' || href === '#next') {
      // Next funnel step
      if (nextPageSlug && workspaceId) {
        navigate(`/f/${workspaceId}/${funnelSlug}/${nextPageSlug}`);
      }
    } else if (href.startsWith('http')) {
      // External URL opens in new tab
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      navigate(href);
    }
  };

  return (
    <div style={{ textAlign: 'center', padding: `${styles?.padding || 16}px`, margin: `${styles?.margin || 0}px` }}>
      <button onClick={handleClick} style={{ ...btnStyle, fontSize: '1.1rem', padding: '14px 36px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, transition: 'opacity 0.2s', minWidth: 200 }}
        onMouseOver={e => e.target.style.opacity = '0.85'}
        onMouseOut={e => e.target.style.opacity = '1'}>
        {content?.text || 'Continue'}
      </button>
    </div>
  );
}

function FunnelForm({ content, styles, funnelSlug, pageSlug, workspaceId, nextPageSlug }) {
  const navigate = useNavigate();
  const [fields, setFields] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/public/form-submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ funnel_slug: funnelSlug, page_slug: pageSlug, data: fields })
      });
    } catch {}
    setSubmitted(true);
    setSubmitting(false);
    
    // Auto-advance to next page after 1.5s
    if (nextPageSlug && workspaceId) {
      setTimeout(() => {
        navigate(`/f/${workspaceId}/${funnelSlug}/${nextPageSlug}`);
      }, 1500);
    }
  };

  if (submitted) {
    return (
      <div style={{ textAlign: 'center', padding: 32, background: '#F0FDF4', borderRadius: 12, margin: 16 }}>
        <p style={{ fontSize: '1.2rem', color: '#15803D', fontWeight: 600 }}>{content?.success_message || content?.successMessage || 'Thank you!'}</p>
      </div>
    );
  }

  return (
    <div style={{ padding: `${styles?.padding || 16}px`, margin: `${styles?.margin || 0}px`, maxWidth: 480, marginLeft: 'auto', marginRight: 'auto' }}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {(content?.fields || []).map((field, i) => (
          <div key={i}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '0.9rem', color: styles?.textColor || '#374151' }}>{field.label || field.name}</label>
            <input
              type={field.type || 'text'}
              placeholder={field.placeholder || field.label}
              required={field.required}
              onChange={e => setFields(prev => ({ ...prev, [field.label || field.name || `field_${i}`]: e.target.value }))}
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #D1D5DB', fontSize: '1rem', boxSizing: 'border-box' }}
            />
          </div>
        ))}
        <button type="submit" disabled={submitting} style={{ background: '#FF6600', color: '#fff', padding: '12px 24px', borderRadius: 8, border: 'none', fontSize: '1rem', fontWeight: 700, cursor: 'pointer', marginTop: 8 }}>
          {submitting ? 'Submitting...' : (content?.submit_text || content?.submitText || 'Submit')}
        </button>
      </form>
    </div>
  );
}

function FunnelCountdown({ content, styles }) {
  const [time, setTime] = useState({ d: 0, h: 0, m: 0, s: 0 });

  useEffect(() => {
    const update = () => {
      const diff = new Date(content?.endDate || Date.now()) - Date.now();
      if (diff <= 0) return setTime({ d: 0, h: 0, m: 0, s: 0 });
      setTime({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000)
      });
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [content?.endDate]);

  const pad = n => String(n).padStart(2, '0');

  return (
    <div style={{ textAlign: 'center', padding: `${styles?.padding || 16}px` }}>
      <div style={{ display: 'inline-flex', gap: 12 }}>
        {[['Days', time.d], ['Hours', time.h], ['Minutes', time.m], ['Seconds', time.s]].map(([label, val]) => (
          <div key={label} style={{ background: styles?.backgroundColor || '#1F2937', color: styles?.textColor || '#fff', borderRadius: 8, padding: '12px 16px', minWidth: 64, textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{pad(val)}</div>
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 2, opacity: 0.7 }}>{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FunnelTestimonial({ content, styles }) {
  return (
    <div style={{ background: styles?.backgroundColor || '#F9FAFB', borderRadius: 12, padding: `${styles?.padding || 24}px`, margin: `${styles?.margin || 8}px`, borderLeft: '4px solid #FF6600' }}>
      <p style={{ fontSize: '1.05rem', fontStyle: 'italic', color: styles?.textColor || '#374151', marginBottom: 16, lineHeight: 1.7 }}>"{content?.quote}"</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {content?.avatar && <img src={content.avatar} alt={content.name} style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover' }} />}
        <div>
          <p style={{ fontWeight: 700, color: '#111827', margin: 0 }}>{content?.name}</p>
          <p style={{ color: '#6B7280', fontSize: '0.85rem', margin: 0 }}>{content?.role}{content?.company && ` @ ${content.company}`}</p>
        </div>
      </div>
    </div>
  );
}

function FunnelPricing({ content, styles }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, justifyContent: 'center', padding: `${styles?.padding || 16}px` }}>
      {(content?.plans || []).map((plan, i) => (
        <div key={i} style={{ background: plan.recommended ? '#FF6600' : '#fff', color: plan.recommended ? '#fff' : '#111827', borderRadius: 12, padding: '24px 28px', minWidth: 220, maxWidth: 280, boxShadow: '0 4px 16px rgba(0,0,0,0.10)', border: plan.recommended ? 'none' : '1px solid #E5E7EB', textAlign: 'center' }}>
          {plan.recommended && <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8, opacity: 0.85 }}>Most Popular</div>}
          <h3 style={{ fontSize: '1.3rem', fontWeight: 700, margin: '0 0 8px 0' }}>{plan.name}</h3>
          <div style={{ fontSize: '2.5rem', fontWeight: 800, margin: '8px 0' }}>${plan.price}<span style={{ fontSize: '1rem', fontWeight: 400 }}>/mo</span></div>
          <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0', textAlign: 'left' }}>
            {(plan.features || []).map((f, j) => <li key={j} style={{ padding: '4px 0', display: 'flex', gap: 8, alignItems: 'center' }}><span>✓</span> {f}</li>)}
          </ul>
          <button style={{ background: plan.recommended ? '#fff' : '#FF6600', color: plan.recommended ? '#FF6600' : '#fff', padding: '10px 24px', borderRadius: 8, border: 'none', fontWeight: 700, cursor: 'pointer', width: '100%', fontSize: '1rem' }}>Get Started</button>
        </div>
      ))}
    </div>
  );
}

function FunnelDivider({ styles }) {
  return <hr style={{ border: 'none', borderTop: `1px solid ${styles?.backgroundColor || '#E5E7EB'}`, margin: `${styles?.margin || 16}px ${styles?.padding || 0}px` }} />;
}

function FunnelPayment({ content, styles, funnelId }) {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resultMsg, setResultMsg] = useState('');

  const handlePay = async () => {
    if (!email) { setError('Please enter your email'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/funnels/${funnelId}/create-payment-order`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ funnel_id: funnelId, email, name })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Payment setup failed'); setLoading(false); return; }
      
      if (data.provider === 'razorpay' && data.key_id) {
        const options = {
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          order_id: data.order_id,
          name: data.description || 'Payment',
          prefill: { email, name },
          handler: async (response) => {
            const verifyRes = await fetch(`${API_URL}/api/funnels/${funnelId}/verify-payment`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                funnel_id: funnelId, email, name,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              })
            });
            const result = await verifyRes.json();
            if (result.success) {
              setSuccess(true);
              setResultMsg(result.message || 'Payment successful!');
            } else {
              setError(result.message || 'Payment verification failed');
            }
            setLoading(false);
          },
          modal: { ondismiss: () => setLoading(false) }
        };
        if (window.Razorpay) {
          new window.Razorpay(options).open();
        } else {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = () => new window.Razorpay(options).open();
          document.body.appendChild(script);
        }
      }
    } catch (e) {
      setError('Payment failed. Please try again.');
      setLoading(false);
    }
  };

  if (success) {
    return <div style={{ textAlign: 'center', padding: 32 }} dangerouslySetInnerHTML={{ __html: resultMsg }} />;
  }

  const currSymbol = {INR:'₹',USD:'$',EUR:'€',GBP:'£'}[content?.currency] || content?.currency || '₹';
  return (
    <div style={{ textAlign: 'center', padding: `${styles?.padding || 24}px`, margin: `${styles?.margin || 0}px` }}>
      <div style={{ maxWidth: 400, margin: '0 auto', background: '#f9fafb', borderRadius: 12, padding: 24, border: '1px solid #e5e7eb' }}>
        <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>{content?.productName || 'Product'}</p>
        <p style={{ fontSize: 36, fontWeight: 800, marginBottom: 16 }}>{currSymbol}{content?.amount || 0}</p>
        <input type="text" placeholder="Your Name" value={name} onChange={e => setName(e.target.value)}
          style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, marginBottom: 8, fontSize: 14, boxSizing: 'border-box' }} />
        <input type="email" placeholder="Your Email" value={email} onChange={e => setEmail(e.target.value)}
          style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, marginBottom: 12, fontSize: 14, boxSizing: 'border-box' }} />
        {error && <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 8 }}>{error}</p>}
        <button onClick={handlePay} disabled={loading}
          style={{ width: '100%', background: '#16a34a', color: '#fff', padding: '14px', borderRadius: 8, border: 'none', fontSize: 16, fontWeight: 700, cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Processing...' : (content?.buttonText || 'Buy Now')}
        </button>
      </div>
    </div>
  );
}

function FunnelComponentRenderer({ component, funnelId, funnelSlug, pageSlug, workspaceId, nextPageSlug }) {
  const { type, content, styles } = component;
  const props = { content, styles, funnelId, funnelSlug, pageSlug, workspaceId, nextPageSlug };

  switch (type) {
    case 'headline': return <FunnelHeadline {...props} />;
    case 'text': return <FunnelText {...props} />;
    case 'image': return <FunnelImage {...props} />;
    case 'video': return <FunnelVideo {...props} />;
    case 'button': return <FunnelButton {...props} />;
    case 'form': return <FunnelForm {...props} />;
    case 'countdown': return <FunnelCountdown {...props} />;
    case 'testimonial': return <FunnelTestimonial {...props} />;
    case 'pricing': return <FunnelPricing {...props} />;
    case 'payment': return <FunnelPayment {...props} />;
    case 'divider': return <FunnelDivider {...props} />;
    default: return null;
  }
}

// ─── Public Funnel Page ───────────────────────────────────────────────────────

export default function PublicFunnelPage() {
  const { workspaceId, funnelSlug, pageSlug } = useParams();
  const navigate = useNavigate();
  const [funnel, setFunnel] = useState(null);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadFunnel = useCallback(async () => {
    try {
      let res;
      const hostname = window.location.hostname;
      const isCustomDomain = hostname !== 'app.salesqualifyd.com' && 
                             !hostname.includes('preview.emergentagent') && 
                             hostname !== 'localhost';
      
      if (isCustomDomain && !workspaceId) {
        // Custom domain: resolve domain → org → funnel
        // URL pattern: https://leads.example.com/{slug} or https://leads.example.com/{slug}/{page_slug}
        const pathParts = window.location.pathname.split('/').filter(Boolean);
        const slug = funnelSlug || pathParts[0];
        const stepSlug = pageSlug || pathParts[1] || '';
        
        if (stepSlug) {
          // Specific step: https://leads.example.com/sqfd-launch/checkout
          res = await fetch(`${API_URL}/api/public/funnel-by-domain/${hostname}/${slug}/${stepSlug}`);
        } else {
          // First page: https://leads.example.com/sqfd-launch
          res = await fetch(`${API_URL}/api/public/funnel-by-domain/${hostname}/${slug}`);
        }
      } else if (workspaceId) {
        // Standard URL: /f/{workspaceId}/{slug}
        res = await fetch(`${API_URL}/api/public/funnel/${workspaceId}/${funnelSlug}`);
      } else {
        throw new Error('Invalid funnel URL');
      }
      
      if (!res.ok) throw new Error(res.status === 404 ? 'Funnel not found or not published yet.' : 'Failed to load funnel.');
      const data = await res.json();
      setFunnel(data);

      const pages = data.pages || [];
      let targetPage = null;
      if (pageSlug) {
        targetPage = pages.find(p => p.slug === pageSlug);
        if (!targetPage) throw new Error('Page not found in this funnel.');
      } else {
        // Show first page that HAS components (skip empty template pages)
        const sortedPages = [...pages].sort((a, b) => (a.order || 0) - (b.order || 0));
        targetPage = sortedPages.find(p => (p.components || []).length > 0) || sortedPages[0] || null;
      }
      setPage(targetPage);

      // Set custom SEO meta tags — NO platform branding
      const pageTitle = targetPage?.seo_title || targetPage?.name || data?.default_seo_title || data?.name || '';
      if (pageTitle) document.title = pageTitle;
      
      // Meta description
      const metaDesc = targetPage?.seo_description || data?.default_seo_description || '';
      if (metaDesc) {
        let descTag = document.querySelector('meta[name="description"]');
        if (!descTag) { descTag = document.createElement('meta'); descTag.name = 'description'; document.head.appendChild(descTag); }
        descTag.content = metaDesc;
      }
      
      // OG tags for social sharing
      const ogImage = targetPage?.og_image || data?.og_image || '';
      if (ogImage) {
        let ogTag = document.querySelector('meta[property="og:image"]');
        if (!ogTag) { ogTag = document.createElement('meta'); ogTag.setAttribute('property', 'og:image'); document.head.appendChild(ogTag); }
        ogTag.content = ogImage;
      }
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) { ogTitle = document.createElement('meta'); ogTitle.setAttribute('property', 'og:title'); document.head.appendChild(ogTitle); }
      ogTitle.content = pageTitle;
      
      // Custom favicon — replace platform favicon with customer's
      const faviconUrl = targetPage?.favicon_url || data?.favicon_url || '';
      if (faviconUrl) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
        link.href = faviconUrl;
        // Also set apple-touch-icon
        let appleLink = document.querySelector("link[rel='apple-touch-icon']");
        if (!appleLink) { appleLink = document.createElement('link'); appleLink.rel = 'apple-touch-icon'; document.head.appendChild(appleLink); }
        appleLink.href = faviconUrl;
      }

      // Track page view
      if (data.id) {
        fetch(`${API_URL}/api/funnels/${data.id}/analytics/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ event_type: 'view', page_slug: targetPage?.slug })
        }).catch(() => {});
      }
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, funnelSlug, pageSlug]);

  useEffect(() => { loadFunnel(); }, [loadFunnel]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F9FAFB' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '4px solid #E5E7EB', borderTopColor: '#FF6600', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          <p style={{ color: '#6B7280' }}>Loading...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F9FAFB' }}>
        <div style={{ textAlign: 'center', maxWidth: 400, padding: 32 }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>🔗</div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', marginBottom: 8 }}>Page Unavailable</h2>
          <p style={{ color: '#6B7280' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F9FAFB' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#6B7280' }}>No pages configured for this funnel yet.</p>
        </div>
      </div>
    );
  }

  const pages = (funnel?.pages || []).sort((a, b) => (a.order || 0) - (b.order || 0));
  const currentIdx = pages.findIndex(p => p.id === page.id);
  const nextPage = pages[currentIdx + 1];
  const prevPage = pages[currentIdx - 1];

  // Use custom code if set
  if (page.custom_code) {
    return <div dangerouslySetInnerHTML={{ __html: page.custom_code }} />;
  }

  const bgColor = page.background_color || '#ffffff';
  const textColor = page.text_color || '#111827';

  return (
    <div style={{ minHeight: '100vh', background: bgColor, color: textColor }}>
      {/* SEO meta could go via Helmet but keeping it simple */}
      {/* Custom CSS */}
      {page.custom_css && <style>{page.custom_css}</style>}

      {/* Page content */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 16px' }}>
        {(page.components || []).map((comp, i) => (
          <FunnelComponentRenderer
            key={comp.id || i}
            component={comp}
            funnelId={funnel.id}
            funnelSlug={funnelSlug}
            pageSlug={page.slug}
            workspaceId={workspaceId}
            nextPageSlug={nextPage?.slug}
          />
        ))}

        {/* Page navigation (subtle) */}
        {/* NO separate navigation buttons — CTA and form submit handle page flow */}
      </div>

      {/* Custom JS */}
      {page.custom_js && <script dangerouslySetInnerHTML={{ __html: page.custom_js }} />}
    </div>
  );
}
